// region Dates

/**
 * Parse date from anything to Date object
 * @param {Date|string|number} srcDate
 *
 * bad time string format  "2014-08-22T12:06:30+0200" (for iOS with spaces)
 * good time string format "2014-08-19T18:59:21+02:00"
 *
 * good time string format "2014-08-22T12:06:30.0200"
 * bad time string format "2014-08-22 12:06:30.0200" (for iOS with spaces)
 */
export const parseDate = (srcDate) => {
    let fixedDate;

    if (typeof srcDate === 'string' && /([-+])\d{4}$/.test(srcDate)) {
        fixedDate = new Date(`${srcDate.slice(0, srcDate.length - 2)}:${srcDate.slice(srcDate.length - 2)}`);
    } else if (typeof srcDate === 'string' && / /.test(srcDate)) {
        fixedDate = new Date(srcDate.replace(' ', 'T'));
    } else if (!(srcDate instanceof Date)) {
        fixedDate = new Date(srcDate);
    } else {
        fixedDate = srcDate;
    }

    return fixedDate;
};

export const mapFormatDate = {
    /**
     * Pad a number with leading zeros
     * @private
     * @param  {number} n
     * @param  {string} placer
     * @param  {number} len
     * @returns {string}
     */
    padZero: (n, placer = '0', len = 2) => `${placer}${n}`.substr(-len),
    a: ({ date }) => date.toDateString().split(' ')[0],
    b: ({ date }) => date.toDateString().split(' ')[1],
    C: ({ date }) => date.getFullYear().toString()
        .substr(0, 2),
    c: ({ date }) => date.toLocaleString(),
    D: ({ date }) => formatDate({ date, format: '%m/%d/%y' }),
    d: ({ date }) => mapFormatDate.padZero(date.getDate()),
    e: ({ date }) => mapFormatDate.padZero(date.getMonth() + 1, ' '),
    E: ({ date, locale }) => {
        let suffix;
        let value;
        let day = date.getMonth() + 1;

        switch (locale) {
            case 'en_US':
                suffix = ['th', 'st', 'nd', 'rd'];
                value = day % 100;
                day += suffix[(value - 20) % 10] || suffix[value] || suffix[0];
                break;
            case 'ru_RU':
                day += '-e';
                break;
            case 'da_DK':
            case 'sv_SE':
                day += ':e';
                break;
            case 'de_DE':
                day += '.';
                break;
            case 'nl_NL':
            case 'fr_FR':
                day += 'e';
                break;
            case 'ro_RO':
                day += 'a';
                break;
            default:
                day = String(day);
                break;
        }

        return day;
    },
    H: ({ date }) => mapFormatDate.padZero(date.getHours()),
    I: ({ date }) => date.getHours() % 12 !== 0 ? mapFormatDate.padZero(date.getHours() % 12) : 12,
    j: ({ date }) => {
        let day = 0;
        let i = 0;
        const len = date.getMonth();

        for (; i < len; i++) {
            day += 30;
        }

        return mapFormatDate.padZero(day + date.getDate(), '0', 3);
    },
    m: ({ date }) => mapFormatDate.padZero(date.getMonth() + 1),
    M: ({ date }) => mapFormatDate.padZero(date.getMinutes()),
    p: ({ date }) => ['AM', 'PM'][Math.floor(date.getHours() / 12)],
    r: ({ date }) => formatDate({ date, format: '%I:%M:%S %p' }),
    R: ({ date }) => formatDate({ date, format: '%H:%M' }),
    S: ({ date }) => mapFormatDate.padZero(date.getSeconds()),
    SSS: ({ date }) => {
        let ms = date.getMilliseconds().toString();

        while (ms.length < 3) {
            ms = ms.concat('0');
        }

        return ms;
    },
    T: ({ date }) => formatDate({ date, format: '%H:%M:%S' }),
    x: ({ date }) => date.toLocaleDateString(),
    X: ({ date }) => date.toLocaleTimeString(),
    y: ({ date }) => date.getFullYear().toString()
        .substr(2),
    Y: ({ date }) => date.getFullYear(),
    Z: ({ date }) => {
        const timeZone = date.getTimezoneOffset();
        return (timeZone > 0 ? '+' : '-') + mapFormatDate.padZero(Math.abs(timeZone), '0', 4);
    }
};

/**
 * Convert Date to a string
 * @private
 * @param  {string} dateStr
 * @param  {Date|string|number} date
 * @param  {string=}            locale
 * @returns {string}
 */

/* eslint-disable func-style */
export function formatDate({ format = '', date, locale }) { // `formatDate` and `mapFormatDate` both have a reference to each other
    return String(format).replace(
        /%%|%(SSS|[abcCdDeEHIjmMprRSTxXyYzZ])/g,
        (match, code) => match === '%%'
            ? '%'
            : mapFormatDate[code]({ date, locale })
    );
}

/* eslint-enable func-style */

// endregion

// region Dates for IMS
export const parseImsDate = srcDate => new Date(`${srcDate.trim().replace(' ', 'T')}Z`);

export const getDateForIms = srcDate => srcDate
    .toISOString()
    .replace(/[TZ]/g, ' ')
    .trim();

export const parseImsBankIdDate = (scrDate) => {
    const decodedDate = decodeURIComponent(scrDate).replace('+', ' ');

    return parseImsDate(decodedDate);
};

// endregion

// region Numbers

export const formatNumber = (number, options = {}) => {
    const parsedNumber = parseFloat(number);

    if (isNaN(parsedNumber)) {
        return '';
    }

    let formattedNumber = options.precision != null
        ? parsedNumber.toFixed(options.precision)
        : parsedNumber.toString();

    const numberParts = formattedNumber.split('.');
    const thGroupsRe = /(\d)(?=(\d{3})+\b)/g;

    numberParts[0] = numberParts[0].replace(thGroupsRe, `$1${options.thSeparator}`);

    formattedNumber = !numberParts[1]
        ? numberParts[0]
        : numberParts.join(options.decSeparator);

    return formattedNumber;
};

export const delocalizeNumber = (str, options = {}) => {
    if (typeof str !== 'string') {
        return str;
    }

    const thSeparatorRegex = new RegExp(`\\${options.thSeparator}`, 'g');
    const decSeparatorRegex = new RegExp(`\\${options.decSeparator}`, 'g');
    const numberStr = str.replace(thSeparatorRegex, '').replace(decSeparatorRegex, '.');
    const result = parseFloat(numberStr);

    if (Number.isNaN(result) || String(result) !== numberStr && !numberStr.endsWith('.00')) {
        return str;
    }

    return result;
};

// endregion

// region Duration

export const getDurationTimeLong = (duration) => {
    const d = parseDate(duration);
    const hh = fp(d.getUTCHours());
    const mm = fp(d.getUTCMinutes());
    const ss = fp(d.getUTCSeconds());

    return `${hh}:${mm}:${ss}`;
};

export const getDurationTimeShort = (duration) => {
    const d = parseDate(duration);

    return `${fp(d.getUTCHours())}:${fp(d.getUTCMinutes())}`;
};

export const formatTimeFromSeconds = (seconds) => {
    const numberOfSeconds = parseInt(seconds) || 0;
    let hoursOut;
    let minutesOut;
    let secondsOut;

    hoursOut = Math.floor(numberOfSeconds / 3600);
    minutesOut = Math.floor((numberOfSeconds - hoursOut * 3600) / 60);
    secondsOut = numberOfSeconds - hoursOut * 3600 - minutesOut * 60;

    if (hoursOut < 10) {
        hoursOut = `0${hoursOut}`;
    }
    if (minutesOut < 10) {
        minutesOut = `0${minutesOut}`;
    }
    if (secondsOut < 10) {
        secondsOut = `0${secondsOut}`;
    }

    return hoursOut !== '00'
        ? `${hoursOut}:${minutesOut}:${secondsOut}`
        : `${minutesOut}:${secondsOut}`;
};

// endregion

// region Other

// format part
export const fp = part => String(part).padStart(2, '0');

// endregion
